import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Display, BootLoader, writeMfaLocalHint } from '@pc/ui';
import { readMfaClaims } from '@pc/db';
import { useAuth } from './AuthProvider';

// /auth/callback
//
// Destination of the hand-off URL the central auth site
// (auth.peptideclients.com) redirects users to after a successful
// sign-in / sign-up / password-reset / invitation flow. The auth site
// packs the Supabase access + refresh tokens into the URL FRAGMENT
// (`#access_token=...&refresh_token=...&type=session`) and the
// originating in-app path into the QUERY STRING
// (`?return_to=/dashboard`).
//
// Why the split:
//
//   - supabase-js's `detectSessionInUrl: true` (enabled by
//     `initSupabase` in @pc/db) AUTO-CONSUMES any `#access_token=…`
//     fragment at app boot, calls `setSession()` itself, fires the
//     `SIGNED_IN` event, AND clears `window.location.hash`. So by the
//     time we run, the session is ALREADY in `pc-web.auth` localStorage
//     and the fragment is gone.
//   - That hash wipe is exactly why `return_to` can't ride in the
//     fragment too -- it'd be erased before we could read it. The
//     query string survives.
//
// This page therefore just waits for `AuthProvider` to settle (i.e.
// the auto-detected session to land in React state), then navigates
// to the validated `return_to` path. On any failure -- including the
// case where the fragment was missing tokens or supabase-js rejected
// them -- we surface an error banner with a link back to the central
// sign-in.
//
// IMPORTANT: this component MUST live OUTSIDE RequireAuth. When we
// arrive here, by definition no session exists in this app yet, so a
// gate that redirects to the central sign-in would bounce us straight
// back to auth.peptideclients.com and we'd never get to consume the
// tokens.
export function AuthCallbackPage() {
  const nav = useNavigate();
  const { session, loading } = useAuth();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (loading) return;
    if (session && session.access_token) {
      const returnTo = safeReturnPath(searchParams.get('return_to'));
      const mfaVerifiedHint = searchParams.get('mfa_verified') === '1';
      const finish = (): void => {
        try {
          window.history.replaceState({}, document.title, returnTo);
        } catch {
          // Same-origin replaceState rarely fails; ignore if it does.
        }
        nav(returnTo, { replace: true });
      };
      if (mfaVerifiedHint) {
        // Capture the JWT session_id BEFORE we hand control to the
        // router so `RequireMfaVerified` can read the hint in the
        // same render cycle. `readMfaClaims()` decodes the
        // app_metadata + session_id claims from the access token we
        // just landed in localStorage; the lookup is purely local on
        // the JWT fast path, so the await is cheap.
        void readMfaClaims().then((claims) => {
          if (claims?.auth_session_id) {
            writeMfaLocalHint(claims.auth_session_id);
          }
        }).catch(() => {
          // If the claim read fails, fall through without a hint.
          // The destination route will fall back to the RPC.
        }).finally(() => {
          finish();
        });
        return;
      }
      finish();
      return;
    }
    setError(
      "Sign-in didn't complete cleanly. Try signing in again from auth.peptideclients.com.",
    );
  }, [session, loading, searchParams, nav]);

  if (error) {
    return (
      <main className="min-h-screen grid place-items-center bg-paper px-4">
        <div className="w-full max-w-md text-center">
          <Display size="md">Sign-in <em>failed</em></Display>
          <p className="mt-3 text-sm text-ink-2">{error}</p>
          <p className="mt-6 text-sm">
            <a
              href="https://auth.peptideclients.com/login"
              className="underline hover:text-clay"
            >
              Back to sign-in
            </a>
          </p>
        </div>
      </main>
    );
  }

  return <BootLoader label="Signing you in…" />;
}

// Validate a same-origin redirect target. Rejects cross-origin /
// protocol-relative / self-loop values, ALWAYS returning a path that
// starts with `/`. The auth site already validated the return_to host;
// here we're just defense-in-depth in case some attacker can squeeze
// a redirect_to past the allow-list.
function safeReturnPath(raw: string | null): string {
  if (!raw) return '/';
  if (typeof raw !== 'string') return '/';
  if (!raw.startsWith('/')) return '/';
  // Reject protocol-relative `//evil.example/foo` -- starts with `/` but
  // a second `/` would smuggle a different origin into react-router.
  if (raw.startsWith('//')) return '/';
  // Reject auth intermediate paths that can cause post-login loops.
  if (raw.startsWith('/auth/callback')) return '/';
  if (raw === '/mfa' || raw.startsWith('/mfa?')) return '/';
  if (raw === '/sign-in' || raw.startsWith('/sign-in?')) return '/';
  return raw;
}
